import { createSlice } from '@reduxjs/toolkit';

import { TProductCategory } from '@lib/core/products/types';
import serviceInstanceSlice, { IServiceInstance } from '@lib/core/service/slices/serviceInstanceSlice';
import { ThirdPartyConnectModalScenario, ThirdPartyConnectModalShown } from '@lib/core/service/types/interface';
import { languages } from '@lib/tools/locale/utils/consts';
import { PRODUCT_CATEGORY_NONE } from '@lib/tools/shared/helpers/consts';

interface IService {
  isSplashScreenPageShown: boolean;
  isTastePathOnboardingShown: boolean;
  isSelectProductTooltipShown: boolean;
  isTasteIdGuideShown: boolean;
  isHostMocked: boolean;
  isDevToolsEnabled: boolean;
  isLocaleEditMode: boolean;
  isFooterTutorialDisplayed: boolean;
  isFunctionalityExplanationDisplayed: boolean;
  productCategory: TProductCategory;
  shouldShowTastePathStartPage: boolean;
  shouldShowScanBarcode: boolean;
  shouldHideDownloadAppCard: boolean;
  isThirdPartyConnectModalShown: ThirdPartyConnectModalShown;
  fidelityCardIdFromKiosk: string;
  thirdPartyConnectModalOpenScenario?: {
    scenario: ThirdPartyConnectModalScenario | '';
    productName: string;
  };
  previousRoute: string;
  route: string;
  lang: string;
  serviceInstance: IServiceInstance;
  isTestResultView: boolean;
}

export const initialState: IService = {
  fidelityCardIdFromKiosk: '',
  isDevToolsEnabled: false,
  isFooterTutorialDisplayed: false,
  isFunctionalityExplanationDisplayed: false,
  isHostMocked: false,
  isLocaleEditMode: false,
  isSelectProductTooltipShown: false,
  isSplashScreenPageShown: false,
  isTasteIdGuideShown: false,
  isTastePathOnboardingShown: false,
  isTestResultView: false,
  isThirdPartyConnectModalShown: {
    location: false,
    rate4: false,
    rate5: false,
    wishlist: false,
  },
  lang: languages.ENGLISH,
  previousRoute: '',
  productCategory: PRODUCT_CATEGORY_NONE,
  route: '',
  serviceInstance: serviceInstanceSlice(undefined, { type: '' }),
  shouldHideDownloadAppCard: false,
  shouldShowScanBarcode: false,
  shouldShowTastePathStartPage: true,
  thirdPartyConnectModalOpenScenario: {
    productName: '',
    scenario: '',
  },
};

export const serviceSlice = createSlice({
  extraReducers: builder => {
    builder.addDefaultCase((state, action) => {
      state.serviceInstance = serviceInstanceSlice(state.serviceInstance, action);
    });
  },
  initialState,
  name: 'service',
  reducers: {
    setFidelityCardIdFromKiosk: (state, action) => {
      state.fidelityCardIdFromKiosk = action.payload;
    },
    setIsDevToolsEnabled(state) {
      state.isDevToolsEnabled = true;
    },
    setIsFooterTutorialDisplayed: (state, action) => {
      state.isFooterTutorialDisplayed = action.payload;
    },
    setIsFunctionalityExplanationDisplayed: (state, action) => {
      state.isFunctionalityExplanationDisplayed = action.payload;
    },
    setIsTasteIdGuideShown: (state, action) => {
      state.isTasteIdGuideShown = action.payload;
    },
    setIsTastePathOnboardingShown(state, { payload }) {
      state.isTastePathOnboardingShown = payload;
    },
    setIsThirdPartyConnectShown: (state, action) => {
      return {
        ...state,
        isThirdPartyConnectModalShown: {
          ...state.isThirdPartyConnectModalShown,
          [action.payload]: true,
        },
      };
    },
    setProductCategory(state, action) {
      state.productCategory = action.payload;
    },
    setSelectProductTooltipShown: state => {
      state.isSelectProductTooltipShown = true;
    },
    setServiceLocale(state, action) {
      state.lang = action.payload;
    },
    setShouldHideDownloadAppCard: state => {
      state.shouldHideDownloadAppCard = true;
    },
    setShowTastePathStartPage(state, action) {
      state.shouldShowTastePathStartPage = action.payload;
    },
    setSplashScreenPageShown: state => {
      state.isSplashScreenPageShown = true;
    },
    setTestResultView(state, { payload }: { payload: boolean }) {
      state.isTestResultView = payload;
    },
    setThirdPartyConnectModalOpenScenario: (state, action) => {
      state.thirdPartyConnectModalOpenScenario = {
        productName: action.payload.productName,
        scenario: action.payload.scenario,
      };
    },
    toggleLocaleEditMode(state, action) {
      state.isLocaleEditMode = action.payload;
    },
    toggleMockHost(state, action) {
      state.isHostMocked = action.payload;
    },
    toggleShowScanBarcode(state, action) {
      state.shouldShowScanBarcode = action.payload;
    },
    writeHistory(state, action) {
      if (state.route !== action.payload) {
        if (!state.previousRoute) {
          return {
            ...state,
            previousRoute: action.payload,
            route: action.payload,
          };
        }
        return {
          ...state,
          previousRoute: state.route,
          route: action.payload,
        };
      }
      return {
        ...state,
      };
    },
  },
});

export default serviceSlice.reducer;

export const {
  setServiceLocale,
  setIsDevToolsEnabled,
  setProductCategory,
  setShowTastePathStartPage,
  setIsTastePathOnboardingShown,
  writeHistory,
  toggleShowScanBarcode,
  toggleLocaleEditMode,
  toggleMockHost,
  setSplashScreenPageShown,
  setIsTasteIdGuideShown,
  setShouldHideDownloadAppCard,
  setThirdPartyConnectModalOpenScenario,
  setIsThirdPartyConnectShown,
  setFidelityCardIdFromKiosk,
  setIsFooterTutorialDisplayed,
  setIsFunctionalityExplanationDisplayed,
  setTestResultView,
  setSelectProductTooltipShown,
} = serviceSlice.actions;
